// Connected apps for one project — every outside service the project talks to,
// as one compact row each: brand tile, status, one small action. Replaces the
// old stack of full-width cards that spent a header, a paragraph and a large
// button per integration; five apps at ~52px a row now fit in the space two
// cards used to take.
//
// WhatsApp and project-level Gmail have no backend yet. They are rendered
// anyway, disabled and labelled "Soon", because the row is the promise of where
// the feature will live — but the button stays unclickable so the UI never
// claims something the API can't do.

import React, { useState } from "react";
import { Loader2, LogOut, Plug, MessageCircle, Mail, Video, Facebook } from "lucide-react";
import toast from "react-hot-toast";
import { Button, Badge } from "../../atoms";
import ConfirmDeleteModal from "../../overlays/ConfirmDeleteModal";
import {
  useProjectMeetAccount,
  useStartMeetConnect,
  useDisconnectMeetAccount,
} from "../../hooks/useProjectMeetAccount";
import {
  useProjectMetaAccount,
  useStartMetaConnect,
  useDisconnectMetaAccount,
} from "../../hooks/useMetaProjectAccount";

// Brand marks as coloured tiles. Brand colours are deliberately literal hex —
// they belong to the brands and must not follow the app theme.
const BRAND = {
  whatsapp: { bg: "#25D366", fg: "#fff", icon: MessageCircle },
  gmail: { bg: "#fff", fg: "#EA4335", icon: Mail, border: true },
  google: { bg: "#4285F4", fg: "#fff", icon: Video },
  facebook: { bg: "#1877F2", fg: "#fff", icon: Facebook },
};

function BrandTile({ brand }) {
  const b = BRAND[brand];
  const Icon = b.icon;
  return (
    <span style={{
      width: 30, height: 30, borderRadius: 8, flexShrink: 0,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      background: b.bg, color: b.fg,
      border: b.border ? "1px solid var(--color-border-subtle)" : "none",
      boxShadow: "0 1px 2px rgba(16,24,40,0.06)",
    }}>
      <Icon size={15} />
    </span>
  );
}

function Row({ brand, name, sub, right, last }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 11,
      padding: "10px 2px",
      borderBottom: last ? "none" : "1px solid var(--color-border-subtle)",
    }}>
      <BrandTile brand={brand} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>{name}</div>
        <div style={{
          fontSize: 11.5, color: "var(--color-text-secondary)", marginTop: 1,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {sub}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>{right}</div>
    </div>
  );
}

export default function IntegrationsPanel({ projectId, showMeta }) {
  const meet = useProjectMeetAccount(projectId);
  const meetStart = useStartMeetConnect();
  const meetDisconnect = useDisconnectMeetAccount(projectId);
  const meta = useProjectMetaAccount(projectId);
  const metaStart = useStartMetaConnect();
  const metaDisconnect = useDisconnectMetaAccount(projectId);

  const [redirecting, setRedirecting] = useState(null); // 'meet' | 'meta'
  const [confirm, setConfirm] = useState(null); // 'meet' | 'meta'

  // Both connects are same-tab OAuth redirects; the sessionStorage pivot lets
  // Settings re-select this project when Google/Facebook sends us back.
  const connect = async (kind) => {
    const startMut = kind === "meet" ? meetStart : metaStart;
    const pivotKey = kind === "meet" ? "crm-meet-connect-project" : "crm-meta-connect-project";
    try {
      setRedirecting(kind);
      const res = await startMut.mutateAsync(projectId);
      if (!res?.authUrl) throw new Error("No auth URL returned");
      try { sessionStorage.setItem(pivotKey, String(projectId)); } catch (_e) {}
      window.location.href = res.authUrl;
    } catch (err) {
      setRedirecting(null);
      toast.error(err?.response?.data?.error || err.message || "Failed to start");
    }
  };

  const disconnect = async () => {
    const mut = confirm === "meet" ? meetDisconnect : metaDisconnect;
    try {
      await mut.mutateAsync();
      toast.success("Disconnected");
      setConfirm(null);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  const statusOf = ({ data, isLoading, isError }) => {
    if (isLoading) return <Loader2 size={13} className="animate-spin" style={{ color: "var(--color-text-muted)" }} />;
    if (isError) return <Badge tone="danger">Error</Badge>;
    return data?.connected ? <Badge tone="success" dot>Connected</Badge> : null;
  };

  const connectBtn = (kind, { isLoading, isError, data }) => {
    if (isLoading || isError) return null;
    if (data?.connected) {
      return (
        <Button variant="secondary" size="sm" leftIcon={<LogOut size={12} />} onClick={() => setConfirm(kind)}>
          Disconnect
        </Button>
      );
    }
    return (
      <Button size="sm" onClick={() => connect(kind)} disabled={redirecting === kind}>
        {redirecting === kind ? "Opening…" : "Connect"}
      </Button>
    );
  };

  const soon = (
    <>
      <Badge tone="neutral">Soon</Badge>
      <Button size="sm" variant="secondary" disabled>Connect</Button>
    </>
  );

  // SOON_ROWS_HIDDEN (2026-08-30, user): rows with no backend yet (WhatsApp,
  // project-level Gmail) are not shown until they work. Flip to true to show
  // them again as disabled "Soon" rows.
  const SHOW_SOON_ROWS = false;
  const rows = [
    ...(SHOW_SOON_ROWS ? [{
      key: "whatsapp", brand: "whatsapp", name: "WhatsApp Business",
      sub: "Message leads on WhatsApp straight from their timeline.",
      right: soon,
    },
    {
      key: "gmail", brand: "gmail", name: "Gmail",
      sub: "A shared sending inbox for this project. Personal Gmail connects under “My Google account”.",
      right: soon,
    }] : []),
    {
      key: "meet", brand: "google", name: "Google Calendar & Meet",
      sub: meet.data?.connected
        ? `Meetings on this project's leads are scheduled from ${meet.data.email}.`
        : "Schedule lead meetings with Meet links from a project account.",
      right: <>{statusOf(meet)}{connectBtn("meet", meet)}</>,
    },
    ...(showMeta ? [{
      key: "meta", brand: "facebook", name: "Facebook Lead Ads",
      sub: meta.data?.connected
        ? `Fetching Instant Form leads from ${meta.data.pageName || meta.data.pageId}.`
        : "Pull Instant Form leads from a Facebook Page into this project.",
      right: <>{statusOf(meta)}{connectBtn("meta", meta)}</>,
    }] : []),
  ];

  return (
    <section style={{ background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, padding: "12px 16px 4px" }}>
      <header style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <Plug size={14} style={{ color: "var(--crm-primary)" }} />
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Connected apps</h3>
        <span style={{ fontSize: 11.5, color: "var(--color-text-muted)" }}>· set up per project</span>
      </header>

      {rows.map((r, i) => (
        <Row key={r.key} {...r} last={i === rows.length - 1} />
      ))}

      <ConfirmDeleteModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={disconnect}
        title={confirm === "meet" ? "Disconnect Google account?" : "Disconnect Facebook Page?"}
        message={confirm === "meet"
          ? `${meet.data?.email || "The Google account"} will be disconnected from this project. New meetings won't be created until you reconnect; past meetings stay intact.`
          : "Leads already imported will stay. New leads will stop syncing to this project."}
        confirmLabel="Disconnect"
        isPending={(confirm === "meet" ? meetDisconnect : metaDisconnect).isPending}
      />
    </section>
  );
}
